import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useThemeStore } from '../../store/theme';
import {
  useInboxSidebarStore,
  EMPLOYEE_INFO,
  formatTimeRemaining,
  isIdeaExpired,
  type InboxTodoItem,
  type InboxIdea,
} from '../../store/inboxSidebar';

// Employee avatar component with actual images
const EmployeeAvatar = ({ employeeId, size = 'md' }: { employeeId: string; size?: 'sm' | 'md' | 'lg' }) => {
  const employee = EMPLOYEE_INFO[employeeId.toLowerCase()] || {
    name: employeeId,
    color: '#6B7280',
    avatar: '',
  };

  const sizeClasses = {
    sm: 'w-10 h-10',
    md: 'w-12 h-12',
    lg: 'w-14 h-14',
  };

  const sizePixels = {
    sm: 40,
    md: 48,
    lg: 56,
  };

  return (
    <div className="relative flex-shrink-0">
      {employee.avatar ? (
        <img
          src={employee.avatar}
          alt={employee.name}
          className={`${sizeClasses[size]} rounded-full object-cover`}
          width={sizePixels[size]}
          height={sizePixels[size]}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            target.nextElementSibling?.classList.remove('hidden');
          }}
        />
      ) : (
        <div
          className={`${sizeClasses[size]} rounded-full flex items-center justify-center font-semibold text-white`}
          style={{ backgroundColor: employee.color }}
        >
          {employee.name.charAt(0).toUpperCase()}
        </div>
      )}
    </div>
  );
};

// Aggregated todo with count
interface AggregatedTodo extends InboxTodoItem {
  count: number;
}

// Simple To-do item matching reference design exactly
const TodoItemSimple = ({ todo, onClick }: { todo: AggregatedTodo; onClick: () => void }) => {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const employee = EMPLOYEE_INFO[todo.employeeId.toLowerCase()];

  // Get display title based on type
  const getDisplayInfo = () => {
    if (todo.type === 'question') {
      return {
        title: todo.count > 1 ? `${todo.count} new questions` : todo.title,
        subtitle: employee?.name || 'Brain AI',
      };
    }
    if (todo.type === 'post_review') {
      return {
        title: todo.count > 1 ? `Review ${todo.count} posts` : todo.title,
        subtitle: employee?.name || 'Social Media Manager',
      };
    }
    return {
      title: todo.title,
      subtitle: employee?.name || todo.employeeId,
    };
  };

  const { title, subtitle } = getDisplayInfo();

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-4 px-4 py-4 transition-colors text-left ${
        isDark ? 'hover:bg-dark-600/50' : 'hover:bg-slate-50'
      }`}
    >
      <EmployeeAvatar employeeId={todo.employeeId} size="sm" />
      <div className="flex-1 min-w-0">
        <p className={`text-[15px] font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
          {title}
        </p>
        <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
          {subtitle}
        </p>
      </div>
    </button>
  );
};

// Idea item matching reference design - "X has an idea" format
const IdeaItemSimple = ({
  idea,
  onAccept,
}: {
  idea: InboxIdea;
  onAccept: () => void;
}) => {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const employee = EMPLOYEE_INFO[idea.employeeId.toLowerCase()];

  return (
    <button
      onClick={onAccept}
      className={`w-full flex items-start gap-4 px-4 py-4 transition-colors text-left ${
        isDark ? 'hover:bg-dark-600/50' : 'hover:bg-slate-50'
      }`}
    >
      <EmployeeAvatar employeeId={idea.employeeId} size="sm" />
      <div className="flex-1 min-w-0">
        <p className={`text-[15px] font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
          {employee?.name || idea.employeeId} has an idea
        </p>
        <p className={`text-sm mt-0.5 line-clamp-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
          {idea.description}
        </p>
        <p className={`text-sm mt-1.5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
          {formatTimeRemaining(idea.expiresAt)}
        </p>
      </div>
    </button>
  );
};

export default function InboxSidebarPanel() {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  const {
    isOpen,
    closePanel,
    todos,
    ideas,
    acceptIdea,
    fetchData,
  } = useInboxSidebarStore();

  // Handle todo click
  const handleTodoClick = (todo: InboxTodoItem) => {
    closePanel();
    navigate(todo.actionUrl);
  };

  // Handle idea accept
  const handleAcceptIdea = async (idea: InboxIdea) => {
    const result = await acceptIdea(idea.id);
    const employeeId = idea.employeeId;
    const baseUrl = result.actionUrl || `/dashboard/employees/${employeeId}`;
    const ideaPrompt = `I'd like to work on this idea: "${idea.title}"\n\n${idea.description}\n\nHelp me get started with this.`;
    const encodedMessage = encodeURIComponent(ideaPrompt);
    const urlWithMessage = `${baseUrl}?message=${encodedMessage}`;
    closePanel();
    navigate(urlWithMessage);
  };

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        // Don't close if clicking on sidebar icons
        const sidebar = document.querySelector('aside');
        if (sidebar && sidebar.contains(event.target as Node)) {
          return;
        }
        closePanel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, closePanel]);

  // Periodically check for expired ideas and refresh data silently
  useEffect(() => {
    if (!isOpen) return;

    const checkExpiredIdeas = () => {
      // Check if any ideas have expired
      const hasExpiredIdeas = ideas.some((idea) => isIdeaExpired(idea.expiresAt));
      if (hasExpiredIdeas) {
        // Silently refresh data to remove expired ideas and potentially generate new ones
        fetchData();
      }
    };

    // Check every 30 seconds while panel is open
    const interval = setInterval(checkExpiredIdeas, 30000);

    return () => clearInterval(interval);
  }, [isOpen, ideas, fetchData]);

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  // Aggregate todos by employee and type for cleaner display
  const aggregatedTodos = todos.reduce((acc, todo) => {
    const key = `${todo.employeeId}-${todo.type}`;
    if (!acc[key]) {
      acc[key] = {
        ...todo,
        count: 1,
      };
    } else {
      acc[key].count += 1;
    }
    return acc;
  }, {} as Record<string, AggregatedTodo>);

  const aggregatedTodoList = Object.values(aggregatedTodos);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={panelRef}
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -20, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className={`fixed top-0 bottom-0 left-[68px] w-[380px] rounded-r-[32px] shadow-2xl z-[45] flex flex-col overflow-hidden ${
            isDark ? 'bg-dark-800' : 'bg-white'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-6 pb-4">
            <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Inbox
            </h2>
            <button
              onClick={closePanel}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-700 text-text-muted' : 'hover:bg-slate-100 text-slate-400'}`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-5 pb-6">
            <div className="space-y-6">
              {/* To-do Section */}
              <div>
                <h3 className={`text-base font-medium mb-3 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  To-do
                </h3>
                {aggregatedTodoList.length > 0 ? (
                  <div
                    className={`rounded-2xl overflow-hidden backdrop-blur-sm ${isDark ? 'bg-white/5' : 'bg-white/80'}`}
                    style={{ boxShadow: isDark ? '0 0 15px rgba(0,0,0,0.3)' : '0 0 15px rgba(0,0,0,0.08)' }}
                  >
                    {aggregatedTodoList.map((todo, index) => (
                      <div key={todo.id}>
                        <TodoItemSimple todo={todo} onClick={() => handleTodoClick(todo)} />
                        {index < aggregatedTodoList.length - 1 && (
                          <div className={`mx-4 ${isDark ? 'border-b border-white/10' : 'border-b border-slate-200/50'}`} />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    className={`rounded-2xl px-4 py-6 text-center backdrop-blur-sm ${isDark ? 'bg-white/5' : 'bg-white/80'}`}
                    style={{ boxShadow: isDark ? '0 0 15px rgba(0,0,0,0.3)' : '0 0 15px rgba(0,0,0,0.08)' }}
                  >
                    <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
                      No tasks right now
                    </p>
                  </div>
                )}
              </div>

              {/* Ideas Section */}
              <div>
                <h3 className={`text-base font-medium mb-3 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  Ideas
                </h3>
                {ideas.length > 0 ? (
                  <div
                    className={`rounded-2xl overflow-hidden backdrop-blur-sm ${isDark ? 'bg-white/5' : 'bg-white/80'}`}
                    style={{ boxShadow: isDark ? '0 0 15px rgba(0,0,0,0.3)' : '0 0 15px rgba(0,0,0,0.08)' }}
                  >
                    {ideas.map((idea, index) => (
                      <div key={idea.id}>
                        <IdeaItemSimple
                          idea={idea}
                          onAccept={() => handleAcceptIdea(idea)}
                        />
                        {index < ideas.length - 1 && (
                          <div className={`mx-4 ${isDark ? 'border-b border-white/10' : 'border-b border-slate-200/50'}`} />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    className={`rounded-2xl px-4 py-6 text-center backdrop-blur-sm ${isDark ? 'bg-white/5' : 'bg-white/80'}`}
                    style={{ boxShadow: isDark ? '0 0 15px rgba(0,0,0,0.3)' : '0 0 15px rgba(0,0,0,0.08)' }}
                  >
                    <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
                      No ideas yet
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
